import React from "react";
import Draggable from "react-draggable";
import classnames from "classnames";
import {
  round,
  calculateCoordinates,
  calculateIndex,
} from "../../../../util/format.js";
import _ from "underscore";

/**
 * Thumb - Component used for the GEX UI component.
 */
export class Thumb extends React.Component {
  constructor(props) {
    super(props);

    /**
     * state
     * @type {Object}
     *The component state consists of the following properties

     * * isDragging
     * * Flag used to identify whether the thumb is in motion or rest.
     * * @type {Boolean}

     * * divStartingCoordinates
     * * To keep track of the div starting x coordinate
     * * @type {float}

     * * translatedIndex
     * * This property is used to keep track of the hovered span's index
     * * @type {integer}

     * * lastIndex
     * * To keep track of the index where the thumb stopped.
     * * @type {float}

     */

    this.state = {
      isDragging: false, // Allow the elemental Drag
      divStartingCoordinates: 0, // InitialPoint (X,Y) for the cursor
      spanLength: 10, // fixed in css - span length is 10px - used for movement in x-axis
      spanHeight: 20, // fixed in css - span height is 20px - used for movement in y-axis
      xTranslatedIndex: 0, // Intermediate index while translating - X
      yTranslatedIndex: 0, // Intermediate index while translating - X
      xLastIndex: 0, // Last index where cursor stopped - X.
      yLastIndex: 0, // Last index where cursor stopped - Y.
    };

    // Create a ref to track thumb movement
    this.insideThumbRef = React.createRef();
  }

  /**
   * Remove the listeners when the component will Unmount
   * handleMouseMove is used to observe the translation and cursor movement
   * handleMouseUp is used to track where the Mouse movement ends
   */

  componentWillUnmount() {
    window.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("mouseup", this.handleMouseUp);
  }

  /**
   * Add the listeners when the component re-renders.
   * handleMouseMove is used to observe the translation and cursor movement
   * handleMouseUp is used to track where the Mouse movement ends
   */

  componentDidmount() {
    window.addEventListener("mousemove", this.handleMouseMove);
    window.addEventListener("mouseup", this.handleMouseUp);
  }

  handleMouseDown = () => {
    this.setState(
      {
        isDragging: true,
      },
      () => {}
    );
  };

  /**
   * handleMove - Event listner when the thumb is being dragged.
   * @param clientX
   */

  handleMove = (...args) => {
    const { x, y } = args[1];
    const {
      spanLength,
      spanHeight,
      xTranslatedIndex,
      yTranslatedIndex,
    } = this.state;
    const { width } = this.props;
    const geneElementInSingleLine = (width - 20) / spanLength; // gives no of elemets that can be accomodated in single line.
    const roundX = round(x, 10);
    const roundY = round(y, 20);
    if (this.state.isDragging) {
      this.setState(
        {
          xTranslatedIndex: roundX,
          yTranslatedIndex: roundY,
          xLastIndex: 0,
          yLastIndex: 0,
        },
        () => {}
      );
    }

    const index = calculateIndex(
      roundX,
      roundY,
      geneElementInSingleLine,
      spanLength,
      spanHeight
    );
    if (index < 11) {
      return false;
    } else {
      this.props.getTranslatedIndex(index);
    }
  };

  mouseUp = (...args) => {
    const { x, y } = args[1];
    const {
      spanLength,
      spanHeight,
      xTranslatedIndex,
      yTranslatedIndex,
    } = this.state;
    const { width } = this.props;
    const geneElementInSingleLine = (width - 20) / spanLength; // gives no of elemets that can be accomodated in single line.
    this.setState(
      {
        isDragging: false,
        xLastIndex: round(x, 10),
        yLastIndex: round(y, 20),
        xTranslatedIndex: 0,
        yTranslatedIndex: 0,
      },
      () => {}
    );
    const index = calculateIndex(
      this.state.xLastIndex,
      this.state.yLastIndex,
      geneElementInSingleLine,
      spanLength,
      spanHeight
    );
    if (index < 11) {
      return false;
    } else {
      this.props.indexCallBack(index);
    }
  };

  render() {
    const {
      thumbInitialPoint,
      width,
      classes,
      left,
      right,
      height,
    } = this.props;
    const { spanLength, spanHeight, xLastIndex } = this.state;
    /**
     * There is a padding of 10px each on left. So 10px is removed
     * to get the right width of the div.
     * No of elements that can fit in one line
     * We can use this to get the first and last index of the gene elements in the single line.
     */

    const geneElementInSingleLine = (width - 20) / spanLength; // gives no of elemets that can be accomodated in single line.
    const startingPoints =
      calculateCoordinates(
        thumbInitialPoint,
        geneElementInSingleLine,
        spanHeight,
        spanLength
      ) || {};

    /**
     * Left Thumb default position when the component did mount.
     * Since the left thumb index should exclude the
     * The default position starts after '{',  1 is added to thumbInitialPoint point.
     * @type {[float]}
     */
    // const initialPoint = (thumbInitialPoint) * spanLength; // If left add + 1 because array starts from `0` index
    return (
      <React.Fragment>
        {left && !right && !_.isEmpty(startingPoints) && (
          <Draggable
            id="drag-thumb"
            bounds="parent"
            grid={[spanLength, spanHeight]}
            defaultPosition={{
              x: startingPoints.startingX,
              y: startingPoints.startingY,
            }}
            handle=".left-thumb"
            ref={this.insideThumbRef}
            onStart={this.handleMouseDown}
            onDrag={this.handleMove}
            onStop={this.mouseUp}
          >
            <img
              className={classnames("left-thumb", classes.leftThumb)}
              draggable={false}
              src={require("../../../../digital-assays/assets/images/left_handle.png")}
            />
          </Draggable>
        )}

        {!left && right && !_.isEmpty(startingPoints) && (
          <Draggable
            id="drag-thumb"
            bounds="parent"
            grid={[spanLength, spanHeight]}
            defaultPosition={{
              x: startingPoints.startingX,
              y: startingPoints.startingY,
            }}
            handle=".right-thumb"
            ref={this.insideThumbRef}
            onStart={this.handleMouseDown}
            onDrag={this.handleMove}
            onStop={this.mouseUp}
          >
            <img
              className={classnames("right-thumb", classes.rightThumb)}
              draggable={false}
              src={require("../../../../digital-assays/assets/images/right_handle.png")}
            />
          </Draggable>
        )}
      </React.Fragment>
    );
  }
}
